import React, { useCallback, useEffect, useState } from 'react';
import { Segmented, Tag } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CountUp from 'react-countup';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';
import axios from 'axios';
import config from '../../config';
import { NdoePage, NdoePageHeader, NdoeActionButton, NdoeError, NdoeLoading } from '../../components/ndoe';
// Pont jetons CSS → canvas : le graphique lit la charte au lieu de figer des
// couleurs. Le jour où la charte bascule, il suit sans être retouché.
import { jetons, alpha, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import './rapports.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);
const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

const Delta = ({ value }) => {
  if (value === null || value === undefined) return <span className="rv-delta rv-delta--na">—</span>;
  const up = value > 0; const flat = value === 0;
  return (
    <span className={`rv-delta ${flat ? '' : up ? 'is-plus' : 'is-moins'}`}>
      {flat ? '=' : up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(value)}%
    </span>
  );
};

// Barre proportionnelle simple (pas de lib de chart pour les classements).
const RankRow = ({ label, n, max, tone }) => (
  <div className="rmod-rank">
    <div className="rmod-rank-head"><span className="rmod-rank-label">{label}</span><b>{n}</b></div>
    <div className="rmod-rank-track"><div className={`rmod-rank-fill${tone ? ' is-sensible' : ''}`} style={{ width: `${max ? Math.round((n / max) * 100) : 0}%` }} /></div>
  </div>
);

const PERIODES = [{ value: 7, label: '7 j' }, { value: 30, label: '30 j' }, { value: 90, label: '90 j' }];

export default function RapportModifications() {
  const role = useSelector((s) => s.user?.currentUser?.role);
  const isAdmin = String(role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();

  const [jours, setJours] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data: d } = await axios.get(`${DOMAIN}/api/audit/modifications/analyse`, { params: { jours } });
      setData(d);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [jours]);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return <NdoePage><NdoeError text="Accès réservé à l’administrateur." /></NdoePage>;

  const maxType = Math.max(1, ...(data?.par_type || []).map((t) => t.n));
  const maxAuteur = Math.max(1, ...(data?.par_auteur || []).map((a) => a.n));
  const serie = data?.par_jour || [];

  // Couleurs lues dans la CHARTE (pont chartTheme), appelées ICI — dans le corps
  // du rendu, jamais au niveau module : au chargement du module le CSS n'est pas
  // garanti appliqué, getComputedStyle rendrait du vide et le pont retomberait
  // EN SILENCE sur ses valeurs de repli.
  // Le trait et son aire portaient deux ors DIFFÉRENTS : #a86f14 pour la courbe,
  // #C9A227 (jeu de jetons mort, supprimé au lot 1) pour le remplissage. Ici une
  // seule source : t.gold, et son aire est la même teinte translucide.
  const t = jetons();
  const chartData = {
    labels: serie.map((j) => j.jour.slice(5)),
    datasets: [{
      label: 'Modifications / jour', data: serie.map((j) => j.n),
      borderColor: t.gold, backgroundColor: alpha(t.gold, 0.15), fill: true, tension: 0.3, pointRadius: 2,
    }],
  };

  // optionsBase apporte la grille, les axes, l'info-bulle aux couleurs de la
  // charte et une animation sobre coupée si l'utilisateur a demandé moins de
  // mouvement au niveau système. `precision: 0` est conservé : on compte des
  // modifications, une graduation à 2,5 n'aurait aucun sens.
  const chartOptions = fusionner(optionsBase(), {
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: t.muted, font: { size: 10.5 } } },
      y: {
        beginAtZero: true,
        grid: { color: t.hairline, drawTicks: false },
        ticks: { color: t.muted, font: { size: 10.5 }, padding: 6, precision: 0 },
        border: { display: false },
      },
    },
  });

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports décisionnels"
        title="Analyse des modifications"
        subtitle="Tendances des changements tracés + zones à suivre (concentrations, volumes anormaux)."
      />
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0 16px' }}>
        <Segmented value={jours} onChange={setJours} options={PERIODES} />
        <NdoeActionButton size="sm" variant="secondary" icon={<UnorderedListOutlined />} onClick={() => navigate('/journal-modifications')}>
          Voir le journal détaillé
        </NdoeActionButton>
      </div>

      {loading ? <NdoeLoading rows={8} />
        : error ? <NdoeError text={error} onRetry={load} />
          : !data ? null
            : (
              <>
                <div className="rmod-stats" style={{ marginBottom: 14 }}>
                  <div className="rmod-stat"><span className="l">Modifications ({data.periode_jours} j)</span>
                    <span className="v"><CountUp end={data.total} duration={0.8} separator=" " /></span>
                    <span className="sub">vs période préc. <Delta value={data.delta_pct} /></span></div>
                  <div className="rmod-stat"><span className="l">Types d’action</span><span className="v">{data.par_type.length}</span></div>
                  <div className="rmod-stat"><span className="l">Contributeurs</span><span className="v">{data.par_auteur.length}</span></div>
                  <div className="rmod-stat rmod-warn"><span className="l">Zones à suivre</span><span className="v">{data.zones_a_suivre.length}</span></div>
                </div>

                {data.zones_a_suivre.length > 0 && (
                  <div className="ndoe-card" style={{ padding: 14, marginBottom: 16 }}>
                    <h3 className="rmod-h3" style={{ marginTop: 0 }}>Zones à suivre</h3>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {data.zones_a_suivre.map((z, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: z.niveau === 'alerte' ? '#fff1f0' : '#fff7e6', border: `1px solid ${z.niveau === 'alerte' ? '#ffccc7' : '#ffe58f'}` }}>
                          <Tag color={z.niveau === 'alerte' ? 'red' : 'gold'}>{z.niveau === 'alerte' ? 'Alerte' : 'À surveiller'}</Tag>
                          <div><b>{z.titre}</b><div style={{ fontSize: 13, color: 'var(--ndoe-muted)' }}>{z.detail}</div></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ndoe-card" style={{ padding: 14, marginBottom: 16 }}>
                  <h3 className="rmod-h3" style={{ marginTop: 0 }}>Volume par jour</h3>
                  <div style={{ height: 240 }}>
                    <Line data={chartData} options={chartOptions} />
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="ndoe-card" style={{ padding: 14 }}>
                    <h3 className="rmod-h3" style={{ marginTop: 0 }}>Par type d’action</h3>
                    {data.par_type.slice(0, 12).map((t) => <RankRow key={t.action} label={t.label} n={t.n} max={maxType} tone={t.sensible} />)}
                  </div>
                  <div className="ndoe-card" style={{ padding: 14 }}>
                    <h3 className="rmod-h3" style={{ marginTop: 0 }}>Par utilisateur</h3>
                    {data.par_auteur.slice(0, 12).map((a) => <RankRow key={a.id} label={a.nom} n={a.n} max={maxAuteur} />)}
                  </div>
                </div>
              </>
            )}
    </NdoePage>
  );
}
